import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { notification, Spin } from 'antd';
import { Icon, Select, Button } from 'Components';
import { useTranslation } from 'react-i18next';
import LMSButton from '../components/Button';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import styles from 'LearningManager/styles/CourseAssignments.module.scss';
import { Assignment } from 'LearningManager/constants/CoursesAssignment.types';
import {
	CourseData,
	LearningPathData,
	LearningPathGroup
} from 'LearningManager/constants/UserDashboard.constants';
import LearningPathCard from 'LearningManager/components/LearningPathCard';
import CourseCard from '../components/CourseCard';
import useDebouncedValue from 'LearningManager/reports/hooks/useDebouncedValue';
import {
	SEARCH_DEBOUNCE_MS,
	toServerSearch
} from 'LearningManager/helpers/search';
import FilterSearch, {
	FilterOption,
	FilterOptionId
} from '../components/FilterSearch';
import ToggleGroup from 'LearningManager/components/ToggleGroup';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import UnassignTrainingModal from 'LearningManager/components/UnassignTrainingModal';
import UnassignScheduledModal, {
	UnassignTarget
} from 'LearningManager/components/UnassignScheduledModal';
import ViewDetailsDrawer from 'LearningManager/components/ViewDetailsDrawer';
import CourseDetailsDrawer from 'LearningManager/components/CourseDetailsDrawer';
import CourseDetailModal from 'LearningManager/components/CourseDetailModal';
import ResetCompletionModal, {
	ResetTarget
} from 'LearningManager/components/ResetCompletionModal';
import BulkActionsBar from 'LearningManager/components/BulkActionsBar';
import {
	resolveAssignmentUserIds,
	UserIdsCache
} from 'LearningManager/components/AssignmentActionFlowModal/resolveAssignmentUserIds';
import BulkResetModal from 'LearningManager/components/BulkResetModal';
import CourseResetModal from 'LearningManager/components/CourseResetModal';
import CourseUnassignModal from 'LearningManager/components/CourseUnassignModal';
import { StepperProvider } from 'LearningManager/hooks/Stepper.context';
import useAssignmentsUserCount from 'LearningManager/hooks/useAssignmentsUserCount';
import {
	ASSIGNMENT_CONCURRENCY,
	CancelledError,
	createLimiter,
	Limiter
} from 'LearningManager/helpers/concurrency';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import useTenantScope from 'LearningManager/hooks/useTenantScope';
import HeaderRow from 'Pages/TenantProvider/components/HeaderRow';
import { useStores } from 'Hooks/useStore';
import { ASSIGNMENT_SOURCES_PAGE_SIZE } from 'Stores/LearningManagerStore';
import { Status } from 'app-types';
import {
	AssignmentSource,
	ListAssignmentSourcesParams
} from 'Services/Api/AssignmentSources/Types';
import {
	assignmentActivityId,
	assignmentLearningPathIds,
	assignmentsForCourse,
	processApiInBatches
} from 'LearningManager/helpers/assignment';
import { LearningPath } from 'Services/Api/LearningManager/Types';
import { Course } from 'Services/Api/Courses/Types';
import {
	filterRolesForUser,
	roleDescription,
	roleDisplayLabel
} from 'LearningManager/helpers/role';
import { getApiErrorMessages } from 'LearningManager/helpers/apiError';
import useLearnerCount from 'LearningManager/hooks/useLearnerCount';
import { ActivityAssignmentStatus } from 'Services/Api/UserDashboard/Types';

const { Option } = Select;

interface CourseItem {
	id: number;
	title: string;
	image: string;
	tags: string[];
	status?: 'Completed' | 'In Progress' | 'Not Started';
}

const PLACEHOLDER_IMAGES = [course1, course2, course3, course4];

const FILTER_SCOPE_LABEL: Record<string, string> = {
	all: 'learning.courseAssignments.filters.organization',
	roles: 'learning.courseAssignments.filters.roles',
	property: 'learning.courseAssignments.filters.property',
	individual: 'learning.courseAssignments.filters.individual'
};

const STATUS_TAG: Record<ActivityAssignmentStatus, string> = {
	completed: 'Complete',
	in_progress: 'In Progress',
	not_started: 'Not started'
};

const buildCourseData = (
	courseId: number,
	isRequired: boolean,
	coursesById: Record<number, Course>,
	imageIndex: number
): CourseData => {
	const course = coursesById[courseId];
	return {
		id: courseId,
		title: course?.name ?? `Course ${courseId}`,
		meta: '',
		tags: [isRequired ? 'Required' : 'Optional'],
		image:
			course?.image_url ||
			PLACEHOLDER_IMAGES[imageIndex % PLACEHOLDER_IMAGES.length]
	};
};

const toAssignment = (
	source: AssignmentSource,
	learningPathsById: Record<number, LearningPath>,
	coursesById: Record<number, Course>
): Assignment => {
	const learningPathGroups: LearningPathGroup[] = assignmentLearningPathIds(
		source.learning_paths
	).map((learningPathId) => {
		const lp = learningPathsById[learningPathId];
		const courses = (lp?.activities ?? []).map((c, idx) =>
			buildCourseData(c.activity_id, c.is_required, coursesById, idx)
		);

		return {
			id: `lp-${learningPathId}`,
			label: lp?.name ?? `Learning Path ${learningPathId}`,
			courses
		};
	});

	const learningPathCourseIds = new Set(
		learningPathGroups.flatMap((g) => g.courses.map((c) => c.id))
	);

	const individualCourses: CourseData[] = source.activities
		.map((activity, idx) => {
			const activityId = assignmentActivityId(activity);

			return activityId === undefined ||
				learningPathCourseIds.has(activityId)
				? undefined
				: buildCourseData(
						activityId,
						activity.is_required,
						coursesById,
						idx
				);
		})
		.filter((course): course is CourseData => !!course);

	const courseCount = learningPathCourseIds.size + individualCourses.length;

	const isScheduled = moment(source.start_date).isAfter(moment(), 'day');

	return {
		id: String(source.id),
		name: source.name,
		type: source.is_assigned_to_future_users ? 'Ongoing' : 'One-Time',
		setupType: source.scopes.includes('group_labels')
			? 'Quick Setup'
			: 'Custom Setup',
		status: isScheduled ? 'Scheduled' :  (STATUS_TAG[source?.status ?? 'in_progress'] || STATUS_TAG.in_progress),
		courseCount,
		dueDate: source.due_date ?? undefined,
		startsDate: source.start_date ?? undefined,
		learningPaths: learningPathGroups.length
			? learningPathGroups
			: undefined,
		individualCourses: individualCourses.length
			? individualCourses
			: undefined
	};
};

const toLearningPathData = (assignment: Assignment): LearningPathData => {
	const completionPercentage = assignment.completionPercentage ?? 0;
	const completedCount =
		assignment.completedCount ??
		Math.round((completionPercentage / 100) * assignment.courseCount);

	return {
		id: assignment.id,
		title: assignment.name,
		statuses: [
			assignment.type,
			...(assignment.status === 'Scheduled' ? ['Scheduled'] : [])
		],
		courseCount: assignment.courseCount,
		completedCount,
		completionPercentage,
		dueDate: assignment.dueDate,
		startsDate: assignment.startsDate,
		courses: assignment.courses ?? [],
		learningPaths: assignment.learningPaths,
		individualCourses: assignment.individualCourses
	};
};

export interface CourseAssignmentOption {
	id: number;
	name: string;
}

const CourseAssignments: React.FC = observer(() => {
	const { t } = useTranslation();
	const { learningManagerStore, assignUsersStore, authStore } = useStores();
	const {
		assignmentSources,
		assignmentSourcesTotal,
		allCoursesTotal,
		learningPathsById,
		coursesById,
		status
	} = learningManagerStore;
	const {
		roles,
		pagedProperties,
		pagedUsers,
		status: audienceStatus
	} = assignUsersStore;
	const isSuperAdmin =
		authStore.currentUser?.primarySystemRole.role === 'superadmin';
	const {
		tenantId: audienceTenantId,
		isTenantResolved
	} = useAudienceTenantId();
	const { globalTenantId } = useTenantScope();

	const [activeTab, setActiveTab] = useState<string>('1');
	const [selectedOrg, setSelectedOrg] = useState<string>('all');
	const [filterIds, setFilterIds] = useState<FilterOptionId[]>([]);
	const [filterSearch, setFilterSearch] = useState('');
	const debouncedFilterSearch = useDebouncedValue(
		toServerSearch(filterSearch),
		SEARCH_DEBOUNCE_MS
	);
	const [isResolvingFilter, setIsResolvingFilter] = useState(false);
	const [hasEmptyFilterAudience, setHasEmptyFilterAudience] = useState(false);
	const filterRequestId = useRef(0);
	const filterTenantId = useRef(globalTenantId);
	const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
	const [checkedCourseIds, setCheckedCourseIds] = useState<Set<number>>(
		new Set()
	);
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [unassignTarget, setUnassignTarget] = useState<Assignment | null>(
		null
	);
	const [detailTargetId, setDetailTargetId] = useState<string | null>(null);
	const [
		courseDetailTarget,
		setCourseDetailTarget
	] = useState<CourseItem | null>(null);
	const [courseDetailModalId, setCourseDetailModalId] = useState<number>();
	const [resetTarget, setResetTarget] = useState<Assignment | null>(null);
	const [
		courseResetTarget,
		setCourseResetTarget
	] = useState<CourseItem | null>(null);
	const [
		courseUnassignTarget,
		setCourseUnassignTarget
	] = useState<CourseItem | null>(null);
	const [
		courseAssignmentTarget,
		setCourseAssignmentTarget
	] = useState<CourseItem | null>(null);
	const [
		courseUnassignSelectTarget,
		setCourseUnassignSelectTarget
	] = useState<CourseItem | null>(null);
	const [showBulkReset, setShowBulkReset] = useState(false);
	const [showBulkUnassign, setShowBulkUnassign] = useState(false);
	const [bulkUserCount, setBulkUserCount] = useState<number>();
	const [isBulkResetting, setIsBulkResetting] = useState(false);
	const [isBulkUnassigning, setIsBulkUnassigning] = useState(false);
	const [isUnassigning, setIsUnassigning] = useState(false);
	const [courseAssignments, setCourseAssignements] = useState<CourseAssignmentOption[]>([]);
	const bulkUserIds = useRef(new Map<string, Promise<string[]>>());
	const bulkLimiter = useRef<Limiter>();
	const history = useHistory();
	const { url } = useRouteMatch();
	const { count, loading, getCount } = useLearnerCount(globalTenantId);

	const courseAssignmentOptions = (courseId?: number) =>
		courseId
			? assignmentsForCourse(
					assignmentSources,
					learningPathsById,
					courseId
			).map((source) => ({ id: source.id, label: source.name }))
			: [];

	const appliedFilterParams = useRef<ListAssignmentSourcesParams>({
		offset: 0
	});
	const appliedFilterKey = useRef('');

	const refreshAssignments = () =>
		learningManagerStore
			.listAssignmentSources(
				{
					...appliedFilterParams.current,
					page_size: Math.min(
						Math.max(
							ASSIGNMENT_SOURCES_PAGE_SIZE,
							learningManagerStore.assignmentSources.length
						),
						1000
					)
				},
				{ silent: true }
			)
			.catch(() => undefined);

	const hasMoreAssignments = learningManagerStore.hasMoreAssignmentSources;
	const isLoadingMoreAssignments =
		status.loadMoreAssignmentSources === Status.Pending;

	const loadMoreAssignments = () =>
		learningManagerStore.loadMoreAssignmentSources(
			appliedFilterParams.current
		);

	const notify = (message: string, tone: 'info' | 'success' = 'info') => {
		notification.open({
			message: t(message),
			placement: 'topRight',
			duration: 3,
			className: 'lms-notification',
			closeIcon: <></>,
			icon: (
				<Icon
					type={tone === 'success' ? 'checkCircle' : 'infoFilled'}
					role="img"
				/>
			),
			description: null
		});
	};

	const resetCourseFromAssignments = async (assignmentIds: number[]) => {
		try {
			await Promise.all(
				assignmentIds.map((id) =>
					learningManagerStore.resetAssignmentSource(id)
				)
			);
			notify('learning.courseAssignments.notifications.courseResetSuccess', 'success');
			refreshAssignments();
		} catch {
			notify('learning.courseAssignments.notifications.resetFailed');
		}
	};

	const unAssignCourses = async (assignmentIds: number[]) => {
		try {
			if(!courseUnassignSelectTarget?.id || !assignmentIds.length) return;
			const payload : any = { assignment_source_ids: assignmentIds }
			if(globalTenantId) payload['tenant_id'] = globalTenantId;
			await learningManagerStore.unAssignCourse(courseUnassignSelectTarget?.id, payload);
			notify('learning.courseAssignments.notifications.courseUnassigned', 'success');
			await learningManagerStore.fetchAllCourses(1000, globalTenantId).catch(() => undefined);
		} catch (error) {
			const msg = getApiErrorMessages(error);
			notify(msg.length ? msg[0] : 'learning.courseAssignments.notifications.unassignFailed');
		} finally {
			setCourseUnassignSelectTarget(null);
		}
	};

	const getAllAssignmentsByCourse = async (courseId: number, isReset: boolean): Promise<CourseAssignmentOption[]> => {
		try {
			if(!courseId) return [];
			const params : any = {}
			if(globalTenantId) params['tenant_id'] = globalTenantId;
			if(isReset) params['has_progress'] = true;
			const response : any = await learningManagerStore.getAllAssignmentsByCourse(courseId, params);
			return response?.data?.data ?? [];
		} catch (error) {
			return [];
		}
	};

	const openCourseReset = async (target: CourseItem) => {
		const assigments = await getAllAssignmentsByCourse(target.id, true);
		setCourseAssignements(assigments);
		if (assigments.length) setCourseAssignmentTarget(target);
		else setCourseResetTarget(target);
	};

	const openCourseUnassign = async (target: CourseItem) => {
		const assigments = await getAllAssignmentsByCourse(target.id, false);
		setCourseAssignements(assigments);
		if (assigments.length) setCourseUnassignSelectTarget(target);
		else setCourseUnassignTarget(target);
	};

	const handleCourseResetConfirm = () => {
		const target = courseResetTarget;
		setCourseResetTarget(null);

		if (!target) return;

		const options = courseAssignmentOptions(target.id);
		resetCourseFromAssignments(options.map((option) => option.id));
	};

	const scopesForAssignment = (assignmentId?: string): string[] => {
		if (!assignmentId) return [];

		const source = assignmentSources.find(
			(s) => String(s.id) === assignmentId
		);

		return source?.scopes ?? [];
	};

	const tenantForAssignment = (
		assignmentId?: string
	): number | undefined => {
		if (!assignmentId) return undefined;

		const source = assignmentSources.find(
			(s) => String(s.id) === assignmentId
		);

		const tenantId = Number(source?.tenant_id);

		return Number.isFinite(tenantId) && tenantId > 0
			? tenantId
			: audienceTenantId;
	};

	const unAssignUsersInBatch = async (assignId: number, userIds: string[]) => {
		await processApiInBatches(userIds, 1000, (batch: string[]) =>
			learningManagerStore.unassignAssignmentSourceUsers(assignId, batch, globalTenantId)
		);
	}

	useEffect(() => {
		if (selectedOrg === 'roles' && !roles.length) {
			assignUsersStore.fetchRoles().catch(() => undefined);
		}
		const pagedFilter =
			selectedOrg === 'property'
				? pagedProperties
				: selectedOrg === 'individual'
				? pagedUsers
				: undefined;
		if (
			pagedFilter &&
			isTenantResolved &&
			(!pagedFilter.items.length ||
				pagedFilter.search !== debouncedFilterSearch ||
				pagedFilter.tenantId !== audienceTenantId)
		) {
			pagedFilter
				.load(audienceTenantId, debouncedFilterSearch)
				.catch(() => undefined);
		}
	}, [selectedOrg, isTenantResolved, audienceTenantId, debouncedFilterSearch]);

	useEffect(() => {
		const requestId = (filterRequestId.current += 1);

		if (filterTenantId.current !== globalTenantId) {
			filterTenantId.current = globalTenantId;

			if (filterIds.length) {
				setFilterIds([]);
				return;
			}
		}

		const applyFilter = async () => {
			const params: ListAssignmentSourcesParams = {
				offset: 0,
				...(globalTenantId ? { tenant_id: globalTenantId } : {})
			};

			let isEmptyAudience = false;

			if (filterIds.length) {
				if (selectedOrg === 'roles') {
					params.role_ids = filterIds.map(Number);
				} else if (selectedOrg === 'individual') {
					params.user_ids = filterIds.map(String);
				} else if (selectedOrg === 'property') {
					setIsResolvingFilter(true);

					try {
						const userIds = await assignUsersStore.fetchUserIdsByGroups(
							filterIds.map(Number)
						);

						if (requestId !== filterRequestId.current) return;

						params.user_ids = userIds;
						isEmptyAudience = userIds.length === 0;
					} catch {
						if (requestId !== filterRequestId.current) return;

						isEmptyAudience = true;
					} finally {
						if (requestId === filterRequestId.current) {
							setIsResolvingFilter(false);
						}
					}
				}
			}

			if (requestId !== filterRequestId.current) return;

			setHasEmptyFilterAudience(isEmptyAudience);

			if (isEmptyAudience) return;

			const filterKey = JSON.stringify(params);

			if (filterKey === appliedFilterKey.current) return;

			appliedFilterKey.current = filterKey;
			appliedFilterParams.current = params;
			setCheckedIds(new Set());

			learningManagerStore
				.listAssignmentSources(params)
				.catch(() => undefined);
		};

		applyFilter();
	}, [selectedOrg, filterIds, globalTenantId]);

	useEffect(() => {
		learningManagerStore
			.fetchAllLearningPaths(globalTenantId)
			.catch(() => undefined);

		learningManagerStore
			.fetchAllCourses(1000, globalTenantId)
			.catch(() => undefined);
	}, [globalTenantId]);

	const computedAssignments = useMemo(
		() =>
			assignmentSources.map((source) =>
				toAssignment(source, learningPathsById, coursesById)
			),
		[assignmentSources, learningPathsById, coursesById]
	);

	useEffect(() => {
		setAssignments(computedAssignments);
	}, [computedAssignments]);

	const hasScheduledSelection = assignments.some(
		(a) => checkedIds.has(a.id) && a.status === 'Scheduled'
	);

	const detailAssignment = detailTargetId
		? assignments.find((a) => a.id === detailTargetId) ?? null
		: null;

	const detailSource = detailTargetId
		? assignmentSources.find((s) => String(s.id) === detailTargetId)
		: undefined;

	const isLoadingAssignments =
		status.listAssignmentSources === Status.Pending || isResolvingFilter;

	const isFilterActive = selectedOrg !== 'all' && filterIds.length > 0;

	const filterOptions: FilterOption[] = useMemo(() => {
		if (selectedOrg === 'roles') {
			return filterRolesForUser(roles).map((role) => ({
				id: role.id,
				label: t(roleDisplayLabel(role.name)),
				desc: t(roleDescription(role.name))
			}));
		}

		if (selectedOrg === 'property') {
			return pagedProperties.items.map((property) => ({
				id: property.id,
				label: property.name,
				desc: property.label
			}));
		}

		if (selectedOrg === 'individual') {
			return pagedUsers.items.map((user) => ({
				id: user.id,
				label: `${user.firstName} ${user.lastName}`.trim(),
				desc: user.email
			}));
		}

		return [];
	}, [selectedOrg, roles, pagedProperties.items, pagedUsers.items]);

	const pagedFilter =
		selectedOrg === 'property'
			? pagedProperties
			: selectedOrg === 'individual'
			? pagedUsers
			: undefined;

	const isLoadingFilterOptions =
		(selectedOrg === 'roles' &&
			audienceStatus.fetchRoles === Status.Pending) ||
		(!!pagedFilter &&
			(pagedFilter.status === Status.Pending ||
				toServerSearch(filterSearch) !== debouncedFilterSearch));

	const hasMoreFilterOptions =
		!!pagedFilter?.hasMore && pagedFilter.loadMoreStatus !== Status.Failure;

	const loadMoreFilterOptions = () =>
		pagedFilter?.loadMore().catch(() => undefined);

	const isLoadingCourses =
		status.loadAllCourses === Status.Pending ||
		status.loadAllCourses === Status.Idle;

	const isSelectionActive = checkedIds.size > 0;

	const courseItems: CourseItem[] = useMemo(
		() =>
			Object.values(coursesById).map((course, idx) => ({
				id: course.id,
				title: course.name,
				tags: [
					course.is_required
						? t('learning.courseAssignments.tags.required')
						: t('learning.courseAssignments.tags.optional')
				],
				image: PLACEHOLDER_IMAGES[idx % PLACEHOLDER_IMAGES.length]
			})),
		[coursesById, isLoadingCourses]
	);

	const handleSelectAll = () => {
		setCheckedIds(new Set(assignments.map((a) => a.id)));
	};

	const handleDeselectAll = () => {
		setCheckedIds(new Set());
	};

	const resolveUsers = (assignmentId: string, cache?: UserIdsCache) =>
		resolveAssignmentUserIds(
			learningManagerStore,
			assignUsersStore,
			Number(assignmentId),
			scopesForAssignment(assignmentId),
			tenantForAssignment(assignmentId),
			cache
		);

	useEffect(() => {
		if (showBulkReset || showBulkUnassign) return;
		bulkLimiter.current?.clear();
	}, [showBulkReset, showBulkUnassign]);

	const activeCourseTarget =
		courseResetTarget ??
		courseAssignmentTarget ??
		courseUnassignTarget ??
		courseUnassignSelectTarget;

	const courseAssignmentResolver = useMemo(() => {
		const cache: UserIdsCache = new Map();
		const limit = createLimiter(ASSIGNMENT_CONCURRENCY);

		return {
			resolve: (assignmentId: number) =>
				limit(() => resolveUsers(String(assignmentId), cache)),
			cancel: limit.clear
		};
	}, [activeCourseTarget?.id]);

	const resolveCourseAssignmentUsers = courseAssignmentResolver.resolve;

	useEffect(() => courseAssignmentResolver.cancel, [
		courseAssignmentResolver
	]);

	const singleCourseTarget = courseResetTarget ?? courseUnassignTarget;

	const {
		count: singleCourseUserCount,
		failedCount: singleCourseFailedCount
	} = useAssignmentsUserCount(
		courseAssignmentOptions(singleCourseTarget?.id).map(
			(option) => option.id
		),
		resolveCourseAssignmentUsers,
		Boolean(singleCourseTarget)
	);

	useEffect(() => {
		if (!singleCourseFailedCount) return;

		setCourseResetTarget(null);
		setCourseUnassignTarget(null);

		notify('learning.courseAssignments.notifications.countLearnersFailed');
	}, [singleCourseFailedCount]);

	const unassignAllUsers = async (assignmentIds: string[]) => {
		const { userIds } = await getCount('assigment', { assignment_source_ids: assignmentIds }, false);
		if (!userIds.length) throw new Error('No users to unassign');
		for (const id of assignmentIds) await unAssignUsersInBatch(Number(id), userIds);
	};

	const handleBulkReset = () => {
		setShowBulkReset(true);
		const ids = Array.from(checkedIds);
		if(ids.length) getCount('assigment', { assignment_source_ids: ids }, true);
	};

	const handleBulkResetConfirm = async () => {
		if (!bulkUserCount || hasScheduledSelection) return;

		const ids = Array.from(checkedIds);
		const failedIds = new Set<string>();

		setIsBulkResetting(true);

		for (const id of ids) {
			try {
				await learningManagerStore.resetAssignmentSource(Number(id));
			} catch {
				failedIds.add(id);
			}
		}

		setIsBulkResetting(false);
		setShowBulkReset(false);
		setCheckedIds(failedIds);

		notify(
			failedIds.size
				? 'learning.courseAssignments.notifications.someTrainingsResetFailed'
				: 'learning.courseAssignments.notifications.assignmentResetSuccess',
			failedIds.size ? 'info' : 'success'
		);

		refreshAssignments();
	};

	const handleBulkUnassign = () => {
		setShowBulkUnassign(true);
		const ids = Array.from(checkedIds);
		if(ids.length) getCount('assigment', { assignment_source_ids: ids }, false);
	};

	const handleBulkUnassignConfirm = async () => {
		const ids = Array.from(checkedIds);
		const failedIds = new Set<string>();
		setIsBulkUnassigning(true);

		try {
			await unassignAllUsers(ids);
		} catch {
			failedIds.add(ids[0]);
		}

		setIsBulkUnassigning(false);
		setShowBulkUnassign(false);
		setCheckedIds(failedIds);

		notify(
			!failedIds.size
				? 'learning.courseAssignments.notifications.trainingUnassigned'
				: failedIds.size === ids.length
				? 'learning.courseAssignments.notifications.unassignFailed'
				: t(
						'learning.courseAssignments.notifications.partialUnassignFailed',
						{
							failed: failedIds.size,
							total: ids.length
						}
				)
		);

		refreshAssignments();
	};

	const { search } = useLocation();
	const params = new URLSearchParams(search);

	const hasNoAssignments =
		!isFilterActive &&
		status.listAssignmentSources === Status.Success &&
		assignments.length === 0;

	const showNoAssignments =
		params.has('showNoAssignments') || hasNoAssignments;

	const showNoFilterMatches =
		params.has('showNoFilterMatches') ||
		(isFilterActive &&
			!isLoadingAssignments &&
			(hasEmptyFilterAudience ||
				(status.listAssignmentSources === Status.Success &&
					assignments.length === 0)));

	const handleCheck = (id: string, checked: boolean) => {
		setCheckedIds((prev) => {
			const next = new Set(prev);
			checked ? next.add(id) : next.delete(id);
			return next;
		});
	};

	const handleMenuAction = (id: string, action: string) => {
		const assignment = assignments.find((item) => item.id === id);

		if (action === 'edit') {
			const setupType = assignment?.setupType ?? 'Quick Setup';

			const params = new URLSearchParams({
				setupType,
				assignmentId: id
			});

			if (assignment?.status === 'Scheduled') {
				params.set('isScheduled', 'true');
			}

			history.push(`${url}/create-assignment?${params.toString()}`);
			return;
		}

		if (action === 'view-details' && assignment) {
			setDetailTargetId(assignment.id);
			return;
		}

		if (action === 'unassign' && assignment) {
			setUnassignTarget(assignment);
			return;
		}

		if (action === 'reset-completion' && assignment) {
			setResetTarget(assignment);
			return;
		}

		console.log('Menu action:', action, 'on assignment:', id);
	};

	const handleConfirmUnassign = async () => {
		if (!unassignTarget) return;

		setIsUnassigning(true);

		try {
			await unassignAllUsers([unassignTarget.id]);
			setCheckedIds((prev) => {
				const next = new Set(prev);
				next.delete(unassignTarget.id);
				return next;
			});

			setUnassignTarget(null);
			notify('learning.courseAssignments.notifications.trainingUnassigned');
			refreshAssignments();
		} catch (error) {
			const msg = getApiErrorMessages(error);
			notify(msg.length ? msg[0] : 'learning.courseAssignments.notifications.unassignFailed');
		} finally {
			setIsUnassigning(false);
		}
	};

	const handleExport = () => {
		console.log('Export triggered');
	};

	const handleNewAssignment = () => {
		history.push(`${url}/create-assignment`);
	};

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<HeaderRow
					title={t('learning.courseAssignments.header.title')}
					subHeading={t(
						'learning.courseAssignments.header.subHeading'
					)}
				>
					{!showNoAssignments && (
						<div className={styles.header__actions}>
							{/* <LMSButton
								variant="border"
								size="small"
								onClick={handleExport}
							>
								{t('learning.courseAssignments.actions.export')}
							</LMSButton> */}

							<Button
								size="small"
								className="cta-prominent"
								type="primary"
								icon={<Icon type="plus" role="img" />}
								onClick={handleNewAssignment}
							>
								{t(
									'learning.courseAssignments.actions.newAssignment'
								)}
							</Button>
						</div>
					)}
				</HeaderRow>
			</div>

			{showNoAssignments ? (
				<div className={styles.emptyState}>
					<div className={styles.emptyState__icon}>
						<Icon type="noAssignments" role="img" />
					</div>

					<div className={styles.emptyState__title}>
						{t('learning.courseAssignments.emptyState.title')}
					</div>

					<div className={styles.emptyState__description}>
						{t('learning.courseAssignments.emptyState.description')}
					</div>

					<LMSButton
						type="primary"
						size="small"
						onClick={handleNewAssignment}
					>
						{t(
							'learning.courseAssignments.actions.createAssignment'
						)}
					</LMSButton>
				</div>
			) : (
				<>
					<div className={styles.filtersContainer}>
						<ToggleGroup
							value={activeTab}
							onChange={(value) => setActiveTab(value)}
							options={[
								{
									key: '1',
									label: t(
										'learning.courseAssignments.tabs.assignments'
									)
								},
								{
									key: '2',
									label: t(
										'learning.courseAssignments.tabs.courses'
									)
								}
							]}
						/>

						<Select
							value={selectedOrg}
							onChange={(val) => {
								setSelectedOrg(val);
								setFilterIds([]);
							}}
							className={styles.orgSelect}
						>
							<Option value="all">
								{t('learning.courseAssignments.filters.all')}
								{isSuperAdmin &&
									` ${t(
										'learning.courseAssignments.filters.organization'
									)}`}
							</Option>

							<Option value="roles">
								{t(
									'learning.courseAssignments.filters.roles'
								)}
							</Option>

							<Option value="property">
								{t(
									'learning.courseAssignments.filters.property'
								)}
							</Option>

							<Option value="individual">
								{t(
									'learning.courseAssignments.filters.individual'
								)}
							</Option>
						</Select>
					</div>

					{selectedOrg !== 'all' && (
						<FilterSearch
							filterType={selectedOrg}
							options={filterOptions}
							selectedIds={filterIds}
							onChange={setFilterIds}
							loading={isLoadingFilterOptions}
							hasMore={hasMoreFilterOptions}
							loadingMore={pagedFilter?.loadMoreStatus === Status.Pending}
							onLoadMore={loadMoreFilterOptions}
							onSearch={pagedFilter ? setFilterSearch : undefined}
						/>
					)}

					{!showNoFilterMatches && (
						<div className={styles.listHeader}>
							<span className={styles.listHeader__label}>
								{activeTab === '1'
									? t(
											'learning.courseAssignments.listHeader.assignmentsBy',
											{
												scope: t(
													FILTER_SCOPE_LABEL[
														selectedOrg
													]
												),
												count: assignmentSourcesTotal
											}
									)
									: t(
											'learning.courseAssignments.listHeader.coursesBy',
											{
												scope: t(
													FILTER_SCOPE_LABEL[
														selectedOrg
													]
												),
												count: allCoursesTotal
											}
									)}
							</span>

							{isSelectionActive && (
								<BulkActionsBar
									selectedCount={checkedIds.size}
									onSelectAll={handleSelectAll}
									onDeselectAll={handleDeselectAll}
									onReset={handleBulkReset}
									onUnassign={handleBulkUnassign}
									resetDisabled={hasScheduledSelection}
									resetDisabledReason={t(
										'learning.courseAssignments.bulkActions.scheduledResetDisabled'
									)}
								/>
							)}
						</div>
					)}

					{showNoFilterMatches ? (
						<div className={styles.emptyState}>
							<div className={styles.emptyState__icon}>
								<Icon
									type="filterNoAssignments"
									role="img"
								/>
							</div>

							<div className={styles.emptyState__title}>
								{t(
									'learning.courseAssignments.filterEmpty.title'
								)}
							</div>

							<div className={styles.emptyState__description}>
								{t(
									'learning.courseAssignments.filterEmpty.description'
								)}
							</div>
						</div>
					) : (
						<>
							{activeTab === '1' && isLoadingAssignments && (
								<div className={styles.loading}>
									<Spin size="large" />
								</div>
							)}

							{activeTab === '1' && !isLoadingAssignments && (
								<InfinityScrollWrapper
									dataLength={assignments.length}
									hasMore={hasMoreAssignments}
									onLoadMore={loadMoreAssignments}
									loading={isLoadingMoreAssignments}
									scrollableId="course-assignments-scroll-container"
									endMessageText=""
								>
									<div className={styles.list}>
										{assignments.map((assignment) => (
											<LearningPathCard
												key={assignment.id}
												learningPath={toLearningPathData(
													assignment
												)}
												checked={checkedIds.has(
													assignment.id
												)}
												onCheck={handleCheck}
												countsOnly
												menuDisabled={isSelectionActive}
												menuOptions={[
													{
														key: 'view-details',
														label: t('learning.courseAssignments.menu.viewDetails')
													},
													{
														key: 'edit',
														label: t('learning.courseAssignments.menu.edit')
													},
													...(assignment.status !==
													'Scheduled'
														? [
																{
																	key: 'reset-completion',
																	label: t('learning.courseAssignments.menu.resetCompletion')
																}
														]
														: []),
													{
														key: 'unassign',
														label: t('learning.courseAssignments.menu.unassign'),
														danger: true
													}
												]}
												onMenuAction={handleMenuAction}
												onCourseClick={(course) =>
													setCourseDetailModalId(
														course.id
													)
												}
											/>
										))}
									</div>
								</InfinityScrollWrapper>
							)}

							{activeTab === '2' && isLoadingCourses && (
								<div className={styles.loading}>
									<Spin size="large" />
								</div>
							)}

							{activeTab === '2' && !isLoadingCourses && (
								<>
									<div className={styles.courseGrid}>
										{courseItems.map((course) => (
											<CourseCard
												key={course.id}
												index={course.id}
												course={course}
												onClick={() =>
													setCourseDetailModalId(
														course.id
													)
												}
												showCheckbox
												checked={checkedCourseIds.has(
													course.id
												)}
												onCheck={(id, isChecked) =>
													setCheckedCourseIds(
														(prev) => {
															const next = new Set(
																prev
															);

															isChecked
																? next.add(id)
																: next.delete(
																		id
																);

															return next;
														}
													)
												}
												showMenu
												onMenuAction={(id, action) => {
													const target = courseItems.find(
														(c) => c.id === id
													);

													if (
														action ===
															'view-details' &&
														target
													) {
														setCourseDetailTarget(
															target
														);
													} else if (
														action ===
															'reset-completion' &&
														target
													) {
														openCourseReset(target);
													} else if (
														action === 'unassign' &&
														target
													) {
														openCourseUnassign(
															target
														);
													} else {
														console.log(
															'Course action:',
															action,
															id
														);
													}
												}}
											/>
										))}
									</div>
								</>
							)}
						</>
					)}
				</>
			)}

			<div className={styles.footer}>
				{t('learning.courseAssignments.footer')}
			</div>

			<StepperProvider>
				<UnassignTrainingModal
					visible={
						Boolean(unassignTarget) &&
						unassignTarget?.status === 'Scheduled'
					}
					onSubmit={handleConfirmUnassign}
					loading={isUnassigning}
					onCancel={() => setUnassignTarget(null)}
				/>

				<UnassignScheduledModal
					visible={
						Boolean(unassignTarget) &&
						unassignTarget?.status !== 'Scheduled'
					}
					assignmentId={Number(unassignTarget?.id ?? 0)}
					scopes={scopesForAssignment(unassignTarget?.id)}
					tenantId={tenantForAssignment(unassignTarget?.id)}
					onCancel={() => setUnassignTarget(null)}
					onContinue={async (
						target: UnassignTarget,
						userIds?: string[]
					) => {
						if (!unassignTarget) return;

						if (target === 'specific') {
							try {
								await unAssignUsersInBatch(Number(unassignTarget.id), userIds ?? []);
								setUnassignTarget(null);
								notify('learning.courseAssignments.notifications.trainingUnassigned');
								refreshAssignments();
							} catch (error) {
								const msg = getApiErrorMessages(error);
								notify(msg.length ? msg[0] : 'learning.courseAssignments.notifications.unassignFailed');
							}
						} else {
							await handleConfirmUnassign();
						}
					}}
				/>

				<ViewDetailsDrawer
					visible={Boolean(detailAssignment)}
					assignment={detailAssignment}
					source={detailSource}
					onClose={() => setDetailTargetId(null)}
				/>

				<CourseDetailsDrawer
					visible={Boolean(courseDetailTarget)}
					course={courseDetailTarget}
					onClose={() => setCourseDetailTarget(null)}
				/>

				<CourseDetailModal
					visible={courseDetailModalId !== undefined}
					activityId={courseDetailModalId}
					onClose={() => setCourseDetailModalId(undefined)}
				/>

				<ResetCompletionModal
					visible={Boolean(resetTarget)}
					assignmentId={Number(resetTarget?.id ?? 0)}
					scopes={scopesForAssignment(resetTarget?.id)}
					tenantId={tenantForAssignment(resetTarget?.id)}
					onCancel={() => setResetTarget(null)}
					onContinue={async (
						target: ResetTarget,
						userIds?: string[]
					) => {
						if (!resetTarget) return;

						try {
							if (target === 'specific') {
								await learningManagerStore.resetAssignmentSourceUsers(
									Number(resetTarget.id),
									userIds ?? [],
									globalTenantId
								);
							} else {
								await learningManagerStore.resetAssignmentSource(
									Number(resetTarget.id)
								);
							}
							notify('learning.courseAssignments.notifications.assignmentResetSuccess', 'success');
							refreshAssignments();
						} catch {
							notify('learning.courseAssignments.notifications.resetFailed');
						} finally {
							setResetTarget(null);
						}
					}}
				/>

				<BulkResetModal
					visible={showBulkReset}
					selectedCount={checkedIds.size}
					userCount={loading ? undefined : count}
					loading={isBulkResetting}
					onCancel={() => setShowBulkReset(false)}
					onConfirm={handleBulkResetConfirm}
				/>

				<UnassignTrainingModal
					visible={showBulkUnassign}
					assignmentCount={checkedIds.size}
					learnerCount={loading ? undefined : count}
					loading={isBulkUnassigning}
					onSubmit={handleBulkUnassignConfirm}
					onCancel={() => setShowBulkUnassign(false)}
				/>

				<BulkResetModal
					visible={Boolean(courseResetTarget)}
					entityType="course"
					selectedCount={0}
					userCount={singleCourseUserCount}
					onCancel={() => setCourseResetTarget(null)}
					onConfirm={handleCourseResetConfirm}
				/>

				<CourseResetModal
					visible={Boolean(courseAssignmentTarget)}
					options={courseAssignments}
					courseId={courseAssignmentTarget?.id ?? 0}
					onCancel={() => setCourseAssignmentTarget(null)}
					onConfirm={(assignmentIds) => {
						setCourseAssignmentTarget(null);
						resetCourseFromAssignments(assignmentIds);
					}}
				/>

				<UnassignTrainingModal
					visible={Boolean(courseUnassignTarget)}
					courseCount={1}
					learnerCount={loading ? undefined : count}
					onSubmit={() => {
						setCourseUnassignTarget(null);
						notify('learning.courseAssignments.notifications.courseUnassigned', 'success');
					}}
					onCancel={() => setCourseUnassignTarget(null)}
				/>

				<CourseUnassignModal
					visible={Boolean(courseUnassignSelectTarget)}
					options={courseAssignments}
					courseId={courseUnassignSelectTarget?.id ?? 0}
					onCancel={() => setCourseUnassignSelectTarget(null)}
					onConfirm={(assignmentIds) => unAssignCourses(assignmentIds)}
				/>
			</StepperProvider>
		</div>
	);
});

export default CourseAssignments;